import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { importApi } from '../api/client';
import { Card, CardHeader, CardTitle, CardBody } from '../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/Table';
import Button from '../components/ui/Button';
import Input, { Select, Label } from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import { getToday } from '../lib/dateUtils';
import { downloadBlob } from '../lib/utils';
import {
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileWarning,
  ArrowRight,
  Info,
  FileDown,
  Building2,
  UserCheck,
} from 'lucide-react';

export default function ImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [options, setOptions] = useState({
    defaultDate: getToday(),
  });

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: (file) => importApi.preview(file, options),
    onSuccess: (response) => {
      setPreviewData(response.data.preview);
    },
    onError: (error) => {
      console.error('Preview error:', error);
      const msg = error.response?.data?.error || t('import.previewError');
      const details = error.response?.data?.details;
      alert(details ? `${msg}\n\n${details}` : msg);
    },
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: (file) => importApi.importExcel(file, options),
    onSuccess: (response) => {
      setImportResult(response.data);
      setShowResultModal(true);
      setPreviewData(null);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    onError: (error) => {
      console.error('Import error:', error);
      const msg = error.response?.data?.error || t('import.importError');
      const details = error.response?.data?.details;
      alert(details ? `${msg}\n\n${details}` : msg);
    },
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewData(null);
      setImportResult(null);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
      setPreviewData(null);
      setImportResult(null);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handlePreview = () => {
    if (selectedFile) {
      previewMutation.mutate(selectedFile);
    }
  };

  const handleImport = () => {
    if (selectedFile) {
      importMutation.mutate(selectedFile);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  // Blank 施工彙整表 template to fill in and import
  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const response = await importApi.downloadTemplate();
      downloadBlob(response.data, '施工彙整表_空白範本.xlsx');
    } catch (error) {
      console.error('Template download error:', error);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('import.title')}</h1>
        <Button variant="outline" onClick={handleDownloadTemplate} isLoading={isDownloadingTemplate}>
          <FileDown className="w-4 h-4 mr-2" />
          {t('dashboard.downloadTemplate', '下載施工彙整表範本Excel')}
        </Button>
      </div>

      {/* Info Card */}
      <Card>
        <CardBody>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tpc-blue flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-900 mb-1">{t('import.templateInfo')}</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('import.hint1')}</li>
                <li>{t('import.hint2')}</li>
                <li>{t('import.hint3')}</li>
                <li>{t('import.hint4')}</li>
              </ul>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('import.uploadFile')}</CardTitle>
        </CardHeader>
        <CardBody>
          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <Label>{t('import.defaultDate')}</Label>
              <Input
                type="date"
                value={options.defaultDate}
                onChange={(e) => setOptions((prev) => ({ ...prev, defaultDate: e.target.value }))}
              />
              <p className="text-xs text-gray-500 mt-1">{t('import.defaultDateHint')}</p>
            </div>
          </div>

          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
              ${selectedFile ? 'border-tpc-blue bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />

            {selectedFile ? (
              <div className="space-y-2">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-tpc-blue" />
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-12 h-12 mx-auto text-gray-400" />
                <p className="font-medium text-gray-700">{t('import.dropzone')}</p>
                <p className="text-sm text-gray-500">{t('import.dropzoneHint')}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-4">
            {selectedFile && (
              <Button variant="secondary" onClick={handleClear}>
                {t('common.reset')}
              </Button>
            )}
            <Button
              onClick={handlePreview}
              disabled={!selectedFile}
              isLoading={previewMutation.isPending}
            >
              {t('import.preview')}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Preview Section */}
      {previewData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileWarning className="w-5 h-5" />
              {t('import.previewTitle')}
            </CardTitle>
          </CardHeader>
          <CardBody>
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{previewData.sheets?.length || 0}</p>
                <p className="text-sm text-gray-500">{t('import.sheetsFound')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{previewData.reportCount || 0}</p>
                <p className="text-sm text-gray-500">{t('import.reportsToImport')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{previewData.totalRows || 0}</p>
                <p className="text-sm text-gray-500">{t('import.totalRows')}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{previewData.duplicates?.length || 0}</p>
                <p className="text-sm text-gray-500">{t('import.duplicates')}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{previewData.newContractors?.length || 0}</p>
                <p className="text-sm text-gray-500">{t('import.newContractors')}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{previewData.newInspectors?.length || 0}</p>
                <p className="text-sm text-gray-500">{t('import.newInspectors')}</p>
              </div>
            </div>

            {/* Sheets Info */}
            {previewData.sheets?.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">{t('import.sheetsInfo')}</h4>
                <div className="flex flex-wrap gap-2">
                  {previewData.sheets.map((sheet, idx) => (
                    <div key={idx} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                      {sheet.name} ({sheet.rowCount} {t('import.rows')}) - {sheet.reportDate}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Contractors & Inspectors Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {previewData.newContractors?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-700 font-medium mb-2">
                    <Building2 className="w-5 h-5" />
                    {t('import.newContractorsToCreate')}
                  </div>
                  <ul className="text-sm text-blue-600 space-y-1">
                    {previewData.newContractors.slice(0, 5).map((name, idx) => (
                      <li key={idx}>• {name}</li>
                    ))}
                    {previewData.newContractors.length > 5 && (
                      <li>... {t('import.andMore', { count: previewData.newContractors.length - 5 })}</li>
                    )}
                  </ul>
                </div>
              )}
              {previewData.newInspectors?.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                    <UserCheck className="w-5 h-5" />
                    {t('import.newInspectorsToCreate')}
                  </div>
                  <ul className="text-sm text-green-600 space-y-1">
                    {previewData.newInspectors.slice(0, 5).map((insp, idx) => (
                      <li key={idx}>
                        • {insp.name} {insp.phone && `(${insp.phone})`} {insp.company && `- ${insp.company}`}
                      </li>
                    ))}
                    {previewData.newInspectors.length > 5 && (
                      <li>... {t('import.andMore', { count: previewData.newInspectors.length - 5 })}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Duplicates Warning */}
            {previewData.duplicates?.length > 0 && (
              <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-orange-700 font-medium mb-2">
                  <AlertCircle className="w-5 h-5" />
                  {t('import.duplicatesWarning')}
                </div>
                <ul className="text-sm text-orange-600 space-y-1">
                  {previewData.duplicates.slice(0, 5).map((dup, idx) => (
                    <li key={idx}>
                      {dup.division} - {dup.projectName} ({dup.reportDate})
                    </li>
                  ))}
                  {previewData.duplicates.length > 5 && (
                    <li>... {t('import.andMore', { count: previewData.duplicates.length - 5 })}</li>
                  )}
                </ul>
              </div>
            )}

            {/* Validation Errors */}
            {previewData.errors?.length > 0 && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                  <XCircle className="w-5 h-5" />
                  {t('import.validationErrors')} ({previewData.errors.length})
                </div>
                <ul className="text-sm text-red-600 space-y-1">
                  {previewData.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx}>
                      {t('import.row')} {err.row}: {err.errors.join(', ')}
                    </li>
                  ))}
                  {previewData.errors.length > 5 && (
                    <li>... {t('import.andMore', { count: previewData.errors.length - 5 })}</li>
                  )}
                </ul>
              </div>
            )}

            {/* Reports Preview Table */}
            {previewData.reports?.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{t('import.reportsPreview')}</h4>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('reports.reportDate')}</TableHead>
                        <TableHead>{t('common.status')}</TableHead>
                        <TableHead>{t('reports.division')}</TableHead>
                        <TableHead>{t('reports.projectItemNo')}</TableHead>
                        <TableHead>{t('reports.projectName')}</TableHead>
                        <TableHead>{t('reports.workLocation')}</TableHead>
                        <TableHead className="text-right">{t('workItems.title')}</TableHead>
                        <TableHead className="text-right">{t('reports.workerCount')}</TableHead>
                        <TableHead>{t('workItems.entryTime')}</TableHead>
                        <TableHead>{t('workItems.exitTime')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.reports.slice(0, 20).map((report, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{report.reportDate}</TableCell>
                          <TableCell>
                            <Badge className={
                              report.calculatedStatus === 'holiday' ? 'bg-orange-100 text-orange-700' :
                              report.calculatedStatus === 'holiday_overtime' ? 'bg-red-100 text-red-700' :
                              report.calculatedStatus === 'overtime' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }>
                              {t(`status.${report.calculatedStatus}`)}
                            </Badge>
                          </TableCell>
                          <TableCell>{report.division}</TableCell>
                          <TableCell>{report.projectItemNo || '-'}</TableCell>
                          <TableCell className="max-w-[200px] truncate" title={report.projectName}>
                            {report.projectName}
                          </TableCell>
                          <TableCell className="max-w-[150px] truncate" title={report.workLocation}>
                            {report.workLocation || '-'}
                          </TableCell>
                          <TableCell className="text-right">{report.workItemCount}</TableCell>
                          <TableCell className="text-right">{report.totalWorkers}</TableCell>
                          <TableCell>{report.entryTime || '-'}</TableCell>
                          <TableCell>{report.exitTime || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {previewData.reports.length > 20 && (
                    <p className="text-sm text-gray-500 text-center py-2">
                      {t('import.showingFirst', { count: 20, total: previewData.reports.length })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Import Button */}
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="secondary" onClick={handleClear}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleImport}
                disabled={previewData.reportCount === 0}
                isLoading={importMutation.isPending}
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                {t('import.importNow')} ({previewData.reportCount - (previewData.duplicates?.length || 0)} {t('import.reports')})
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Result Modal */}
      <Modal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        title={t('import.resultTitle')}
        size="md"
      >
        {importResult && (
          <div className="space-y-4">
            {/* Success Summary */}
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
              <div>
                <p className="font-medium text-green-800">
                  {t('import.importSuccess', { count: importResult.result?.imported || 0 })}
                </p>
                <p className="text-sm text-green-600">
                  {t('import.workItemsImported', { count: importResult.result?.totalWorkItems || 0 })}
                </p>
              </div>
            </div>

            {/* New Contractors/Inspectors */}
            {(importResult.result?.newContractors?.length > 0 || importResult.result?.newInspectors?.length > 0) && (
              <div className="grid grid-cols-2 gap-4">
                {importResult.result?.newContractors?.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-800">
                      {t('import.contractorsCreated', { count: importResult.result.newContractors.length })}
                    </p>
                  </div>
                )}
                {importResult.result?.newInspectors?.length > 0 && (
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-green-800">
                      {t('import.inspectorsCreated', { count: importResult.result.newInspectors.length })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Skipped */}
            {importResult.result?.skipped > 0 && (
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="font-medium text-orange-800">
                  {t('import.skippedCount', { count: importResult.result.skipped })}
                </p>
                {importResult.result.skippedReasons?.slice(0, 3).map((reason, idx) => (
                  <p key={idx} className="text-sm text-orange-600">
                    {reason.division} - {reason.projectName}: {reason.reason}
                  </p>
                ))}
              </div>
            )}

            {/* Validation Errors */}
            {importResult.result?.validationErrors?.length > 0 && (
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="font-medium text-red-800">
                  {t('import.errorCount', { count: importResult.result.validationErrors.length })}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="secondary" onClick={() => setShowResultModal(false)}>
                {t('common.close')}
              </Button>
              <Button onClick={() => { setShowResultModal(false); navigate('/reports'); }}>
                {t('import.viewReports')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
